// src/features/editor/videosApi.ts
import { api } from '@/store/api'

export interface CreateVideoResponse {
  id: string
  endpoint: string
}

// Минимальная форма для авторинга: нас интересует статус обработки.
export interface KinescopeVideo {
  id: string
  status: string
  progress: number
  chapters?: ChapterList | null
}

export interface Poster {
  id: string
  original: string
  active: boolean
  md: string
  sm: string
  xs: string
}

export interface Chapter {
  time: number
  title: string
}

export interface ChapterList {
  items: Chapter[]
  enabled: boolean
}

export const videosApi = api.injectEndpoints({
  endpoints: (build) => ({
    createVideo: build.mutation<
      CreateVideoResponse,
      { filesize: number; title: string; filename: string }
    >({
      query: (body) => ({ url: 'videos/', method: 'POST', body }),
    }),
    getVideo: build.query<KinescopeVideo, string>({
      query: (id) => ({ url: `videos/${id}` }),
      providesTags: (_r, _e, id) => [{ type: 'Video', id }],
    }),
    // block_id обязателен на academy-dev (несмотря на доку, где он «опционален»).
    // Резолвинг видео идёт по videoId, block_id — это id владельца-блока.
    deleteVideo: build.mutation<void, { id: string; blockId: number }>({
      query: ({ id, blockId }) => ({
        url: `videos/${id}`,
        method: 'DELETE',
        params: { block_id: blockId },
      }),
    }),
    getPosters: build.query<Poster[], string>({
      query: (videoId) => ({ url: `videos/${videoId}/posters` }),
      transformResponse: (r: { posters: Poster[] }) => r.posters,
      providesTags: (_r, _e, videoId) => [{ type: 'Poster', id: videoId }],
    }),
    uploadPoster: build.mutation<Poster, { videoId: string; file: File }>({
      query: ({ videoId, file }) => {
        const body = new FormData()
        body.append('poster', file)
        return { url: `videos/${videoId}/posters`, method: 'POST', body }
      },
      invalidatesTags: (_r, _e, { videoId }) => [{ type: 'Poster', id: videoId }],
    }),
    setActivePoster: build.mutation<Poster, { videoId: string; posterId: string }>({
      query: ({ videoId, posterId }) => ({
        url: `videos/${videoId}/posters/${posterId}/active`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { videoId }) => [{ type: 'Poster', id: videoId }],
    }),
    deletePoster: build.mutation<void, { videoId: string; posterId: string }>({
      query: ({ videoId, posterId }) => ({
        url: `videos/${videoId}/posters/${posterId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { videoId }) => [{ type: 'Poster', id: videoId }],
    }),
    setChapters: build.mutation<
      ChapterList,
      { videoId: string; items: Chapter[]; enabled: boolean }
    >({
      query: ({ videoId, items, enabled }) => ({
        url: `videos/${videoId}/chapters`,
        method: 'POST',
        body: { items, enabled },
      }),
      invalidatesTags: (_r, _e, { videoId }) => [{ type: 'Video', id: videoId }],
    }),
  }),
})

export const {
  useCreateVideoMutation,
  useGetVideoQuery,
  useDeleteVideoMutation,
  useGetPostersQuery,
  useUploadPosterMutation,
  useSetActivePosterMutation,
  useDeletePosterMutation,
  useSetChaptersMutation,
} = videosApi
